import React from 'react';
import {
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Ungroup,
  Move,
  Maximize2,
  RotateCw,
  Droplets,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

interface GroupProperties {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  opacity?: number;
  locked?: boolean;
  hidden?: boolean;
}

interface GroupPropertiesPanelProps {
  groupId: string | null;
  properties: GroupProperties;
  onPropertiesChange: (properties: Partial<GroupProperties>) => void;
  onUngroup: () => void;
  onClose: () => void;
}

const PropertyInput: React.FC<{
  label: string;
  value: number;
  onChange: (val: number) => void;
  width?: string;
  unit?: string;
}> = ({ label, value, onChange, width = "w-11", unit }) => (
  <div className="flex items-center h-7 px-2 rounded-lg bg-gray-50 border border-transparent hover:border-gray-200 focus-within:border-[#30A84B] focus-within:bg-white focus-within:shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all group relative">
    <span className="text-[9px] font-black text-gray-400 mr-1.5 select-none uppercase tracking-widest group-focus-within:text-[#30A84B]">
      {label}
    </span>
    <input
      type="number"
      value={Math.round(value)}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className={`${width} bg-transparent text-[11px] font-bold text-gray-700 outline-none text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pl-0.5`}
    />
    {unit && <span className="text-[10px] text-gray-400 font-bold ml-0.5">{unit}</span>}
    <div className="flex flex-col ml-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
      <button
        onClick={(e) => { e.stopPropagation(); onChange(value + 1); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronUp className="w-2.5 h-2.5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onChange(value - 1); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
    </div>
  </div>
);

export const GroupPropertiesPanel: React.FC<GroupPropertiesPanelProps> = ({
  groupId,
  properties,
  onPropertiesChange,
  onUngroup,
}) => {
  if (!groupId) return null;

  const updateProperty = (key: keyof GroupProperties, value: any) => {
    onPropertiesChange({ [key]: value });
  };

  return (
    <div
      className="flex items-center h-9 px-1 gap-0.5"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Label */}
      <div className="px-2 text-[10px] font-black text-[#30A84B] uppercase tracking-widest border-r border-gray-100 mr-1 select-none">
        Group
      </div>

      {/* Position Section */}
      <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Position">
        <Move className="w-3.5 h-3.5 text-gray-400" />
        <div className="flex items-center gap-1">
          <PropertyInput
            label="X"
            value={properties.x}
            onChange={(val) => updateProperty('x', val)}
          />
          <PropertyInput
            label="Y"
            value={properties.y}
            onChange={(val) => updateProperty('y', val)}
          />
        </div>
      </div>

      {/* Size Section */}
      <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Size">
        <Maximize2 className="w-3.5 h-3.5 text-gray-400" />
        <div className="flex items-center gap-1">
          <PropertyInput
            label="W"
            value={properties.width}
            onChange={(val) => updateProperty('width', val)}
            width="w-12"
          />
          <PropertyInput
            label="H"
            value={properties.height}
            onChange={(val) => updateProperty('height', val)}
            width="w-12"
          />
        </div>
      </div>

      {/* Rotation Section */}
      <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Rotation">
        <RotateCw className="w-3.5 h-3.5 text-gray-400" />
        <PropertyInput
          label=""
          value={properties.rotation || 0}
          onChange={(val) => updateProperty('rotation', val)}
          unit="°"
          width="w-10"
        />
      </div>

      {/* Opacity Section */}
      <div className="flex items-center gap-2 px-3 border-r border-gray-100 mr-1" title="Opacity">
        <Droplets className="w-3.5 h-3.5 text-gray-400" />
        <input
          type="range"
          value={properties.opacity || 100}
          onChange={(e) => updateProperty('opacity', parseInt(e.target.value))}
          min="0"
          max="100"
          className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#30A84B] hover:accent-green-600"
        />
        <span className="text-[11px] font-bold text-gray-500 w-7 text-right">
          {Math.round(properties.opacity || 100)}%
        </span>
      </div>

      {/* Control Actions */}
      <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
        <button
          onClick={() => updateProperty('locked', !properties.locked)}
          className={`p-1.5 rounded-md transition-all ${properties.locked ? 'bg-green-50 text-green-600' : 'text-gray-400 hover:bg-gray-100'}`}
          title={properties.locked ? 'Unlock' : 'Lock'}
        >
          {properties.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => updateProperty('hidden', !properties.hidden)}
          className={`p-1.5 rounded-md transition-all ${properties.hidden ? 'bg-gray-50 text-gray-500' : 'text-gray-400 hover:bg-gray-100'}`}
          title={properties.hidden ? 'Show' : 'Hide'}
        >
          {properties.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Group Actions */}
      <div className="flex items-center pl-1">
        <button
          onClick={onUngroup}
          className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-gray-500 hover:text-green-600 hover:bg-green-50 transition-all text-[12px] font-bold"
          title="Ungroup elements"
        >
          <Ungroup className="w-3.5 h-3.5" />
          <span>Ungroup</span>
        </button>
      </div>
    </div>
  );
};
